import { FormGroup } from '@angular/forms';
import { DatePickerTypeView } from 'primeng/datepicker';
import { FormField } from './form.interface';

export interface Input<T> {
    type: InputType;
    isEditable: boolean;
    placeholder?: string;
    data?: T | DropdownData<T>;
    defaultValue?: string | boolean | number | Date | T | null | undefined;
    maxLength?: number;
    style?: string;
    optionLabel?: string;
    optionValue?: string;
    textConfiguration?: TextConfiguration;
    calendarConfiguration?: CalendarConfiguration;
    selectButtonConfiguration?: SelectButtonConfiguration<T>;
    dropdownConfiguration?: DropdownConfiguration<T>;
    textMaskConfiguration?: TextMaskConfiguration;
    twoFieldsConcatConfiguration?: TwoFieldsConcatConfiguration;
    textAreaConfiguration?: TextAreaConfiguration;
    repeatableGroupConfiguration?: RepeatableGroupConfiguration;
}

export interface DropdownData<T> {
    values: Value<T>[];
    filtered: FilteredValue<T>[];
    selected: T | null;
}

export interface Value<T> {
    value: T | null;
    label: string;
}

export interface FilteredValue<T> {
    value: T | null;
    label: string;
    tipo?: string;
    unidades?: number;
}

export interface TextConfiguration {
    regex: RegExp | null | undefined;
}

export interface CalendarConfiguration {
    calendarView: DatePickerTypeView;
    calendarDateFormat: string;
    disabledDates?: Date[];
    onSelect?: (date: Date) => void;
}

export interface SelectButtonConfiguration<T> {
    isDisabled: boolean;
    isMultiSelect: boolean;
    onClick: (arg0: T) => Promise<object>;
}

export interface DropdownConfiguration<T> {
    summation?: {
        summand: string;
        name: string;
        uds: string;
        format?: (total: number) => string;
    };
    hasMaxHeight?: boolean;
    valuePrimaryKey?: string;
    filter?: (it: FilteredValue<T>) => boolean | undefined;
    filteredValues?: FilteredValue<T>[];
    swap?: (it: FormField<T>) => void;
    appendData?: (it: FormField<T>) => void;
    transform?: ((value: FormField<T>, model: FormGroup) => T)[];
}

export interface TextMaskConfiguration {
    mask: string;
}

export interface TwoFieldsConcatConfiguration {
    subField1: string;
    separator: string;
    subField2: string;
}

export interface TextAreaConfiguration {
    rows?: number;
    cols?: number;
    hasVoiceTranscription: boolean;
    canTakePhoto: boolean;
    photoField: string | undefined;
}

export type RepeatableSubFieldContext = {
    rowIndex: number;
    row: Record<string, unknown>;
    rows: Record<string, string>[];
    model: FormGroup;
};

export type RepeatableSubField = {
    key: string;
    label?: string;
    input: Input<unknown>;
    isVisible?: () => boolean;
    computeValue?: (ctx: RepeatableComputeContext) => string;
    getData?: (ctx: RepeatableSubFieldContext) => DropdownData<unknown>;
};

export type RepeatableGroupConfiguration = {
    rows: number;
    addButtonLabel: string;
    bindings: Record<string, string>;
    serialization: {
        separator: string;
    };
    subFields: RepeatableSubField[];
};

export type RepeatableComputeContext = {
    rowIndex: number;
    rows: Record<string, string>[];
    model: FormGroup;
};

export enum InputType {
    HIDDEN,
    EMPTY,
    EXTERNAL,
    ONLY_TEXT,
    TEXT,
    NUMERIC,
    PASSWORD,
    TEXT_MASK,
    TWO_FIELDS_CONCAT,
    TEXTAREA,
    DROPDOWN,
    DROPDOWN_SEARCH,
    MULTISELECT,
    MULTISELECT_SEARCH,
    TIME,
    CALENDAR,
    SWITCH,
    SELECT_BUTTON,
    REPEATABLE_GROUP,
}